/* eslint-disable max-lines */
import { CSSProperties, useRef, useState } from 'react';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import classNames from 'classnames';
import { useDispatch } from 'src/store/store';
import { setAlert } from 'src/components/alert/actions';
import Button from 'src/components/buttons/button';
import i18n from 'src/utils/translate';
import { ModalType } from 'src/constants/modalTypes';
import { showModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import { captureException } from '@sentry/nextjs';
import useDebouncedState from 'src/utils/hooks/useDebouncedState';

type Props = {
  currentImageUrl: string;
  onSave: (id: number | string, formData: FormData) => Promise<{ imageUrl: string }>;
  boundary?: { height: number, width: number };
  className?: string;
  clickTypeLabel?: string;
  communitySlug?: string;
  fitBest?: boolean;
  fitHeight?: boolean;
  fitWidth?: boolean;
  isEditProfile?: boolean;
  userId?: number;
  actionsClassName?: string;
  dataTestId?: string;
  buttonText?: string;
};
export default function ImageUpload({
  boundary = { height: 130, width: 130 },
  clickTypeLabel = '',
  communitySlug = null,
  fitBest = false,
  fitHeight = false,
  fitWidth = false,
  isEditProfile = false,
  userId = null,
  currentImageUrl: currentImageUrlProp,
  className,
  onSave,
  actionsClassName,
  dataTestId,
  buttonText,
}: Props) {
  const dispatch = useDispatch();

  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageScale, setImageScale] = useState({ width: 1, height: 1 });
  const [currentImageUrl, setCurrentImageUrl] = useState(currentImageUrlProp);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
  const [isWidthMaster, setIsWidthMaster] = useState(false);
  const [isHeightMaster, setIsHeightMaster] = useState(false);
  const [fileUpload, setFileUpload] = useState(null);
  const [lastPos, setLastPos] = useDebouncedState({ x: 0, y: 0 });

  const dropzoneRef = useRef<DropzoneRef>();

  const handleImageUpload = (files) => {
    try {
      const imageUrl = URL.createObjectURL(files[0]);
      setPreviewImageUrl(imageUrl);
      setFileUpload(files[0]);
    } catch (ex) {
      captureException(ex);
      dispatch(setAlert(i18n.t('There was a problem uploading your image.')));
    }
  };

  const onPreviewImageLoaded = (event) => {
    const { naturalWidth, naturalHeight } = event.target;

    let imageWidthScale = 1;
    let imageHeightScale = 1;

    // - 'fit best' mode works out the smallest an image can go while remaining ratio-maintaining
    // to always cover the boundary area
    // - 'fit width' will ensure the width is always 100% and adjust the height accordingly
    // - 'fit height' does the same but with height ofc.
    if (fitBest) {
      imageWidthScale = boundary.width / naturalWidth;
      imageHeightScale = boundary.height / naturalHeight;
      if (imageWidthScale >= imageHeightScale) {
        setIsWidthMaster(true);
        imageHeightScale = imageWidthScale;
      } else {
        setIsHeightMaster(true);
        imageWidthScale = imageHeightScale;
      }
    } else {
      // Calculate width scale (and modify height for ratio)
      if (fitWidth) {
        imageWidthScale = boundary.width / naturalWidth;
        setIsWidthMaster(true);

        // Keep ratio
        if (!fitHeight) {
          imageHeightScale = imageWidthScale;
        }
      }
      // Calculate height scale (and modify width for ratio)
      if (fitHeight) {
        imageHeightScale = boundary.height / naturalHeight;
        setIsHeightMaster(true);

        // Keep ratio
        if (!fitWidth) {
          imageWidthScale = imageHeightScale;
        }
      }
    }

    setImageDimensions({ width: naturalWidth, height: naturalHeight });
    setImageScale({ width: imageWidthScale, height: imageHeightScale });
  };

  const formattedFileData = () => {
    const { x, y, width, height } = getFinalCoordinates();
    const formData = new FormData();
    formData.append('file', fileUpload);
    formData.append('x', String(x));
    formData.append('y', String(y));
    formData.append('width', String(width));
    formData.append('height', String(height));
    return formData;
  };

  const saveAndCrop = async () => {
    try {
      if (isEditProfile) {
        const { imageUrl } = await onSave(userId, formattedFileData());
        setCurrentImageUrl(imageUrl);
        setPreviewImageUrl(null);
        setFileUpload(null);
        trackFootfallEvent(Event.CLICKED, { clickType: `added-${clickTypeLabel}` });
      } else {
        const { imageUrl } = await onSave(communitySlug, formattedFileData());
        setCurrentImageUrl(imageUrl);
        setPreviewImageUrl(null);
        setFileUpload(null);
      }
    } catch (ex) {
      captureException(ex);
      dispatch(setAlert(i18n.t('There was a problem uploading your image.')));
    }
  };

  const getFinalCoordinates = () => {
    const width = !isWidthMaster
      ? boundary.width / imageScale.width
      : imageDimensions.width;
    const height = !isHeightMaster
      ? boundary.height / imageScale.height
      : imageDimensions.height;

    return {
      x: Math.round(-imagePos.x / imageScale.width),
      y: Math.round(-imagePos.y / imageScale.height),
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  const onImageMouseDown = event => {
    setIsGrabbing(true);

    // Bind event handlers
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    event.preventDefault();
  };

  const onMouseMove = (event) => {
    // Calculate min x/y values for dragging (taking into account if we are scaling the image)
    const minX = isWidthMaster
      ? 0
      : boundary.width - (imageDimensions.width * imageScale.width);
    const minY = isHeightMaster
      ? 0
      : boundary.height - (imageDimensions.height * imageScale.height);

    setImagePos({
      x: Math.min(Math.max(imagePos.x + (event.clientX - lastPos.x), minX), 0),
      y: Math.min(Math.max(imagePos.y + (event.clientY - lastPos.y), minY), 0),
    });

    // Set the new image pos taking into account the delta of this move
    setLastPos({ x: event.clientX, y: event.clientY });
  };

  const onMouseUp = () => {
    setIsGrabbing(false);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  const triggerPrivacyTipsModal = (event) => {
    if (!isEditProfile) return;
    event.stopPropagation();
    trackFootfallEvent(Event.CLICKED, { clickType: `clicked-add-${clickTypeLabel}` });
    dispatch(showModal({
      modalType: ModalType.PrivacyTipsImages,
      modalProps: { onAccept: () => dropzoneRef.current?.open() },
    }));
  };

  function renderActionButtons() {
    const className = classNames({
      'uploader__actions': true,
      'uploader__actions--current': !previewImageUrl,
      'uploader__actions--preview': !!previewImageUrl,
      [actionsClassName]: !!actionsClassName,
    });
    return (
      <div className={className}>
        {previewImageUrl
          ? (
            <>
              <Button
                text={i18n.t('Save')}
                onClick={saveAndCrop}
              />
              <Button
                text={i18n.t('Cancel')}
                type="secondary"
                onClick={() => {
                  setPreviewImageUrl(null);
                  setImagePos({ x: 0, y: 0 });
                }}
              />
            </>
          ) : (
            <Button
              dataTestId={dataTestId}
              icon="plus-2"
              text={buttonText || i18n.t('Upload')}
              type="secondary"
            />
          )}
      </div>
    );
  }

  function renderPreview() {
    const imageStyles: CSSProperties = {
      position: 'absolute',
      top: imagePos.y,
      left: imagePos.x,
      width: isWidthMaster ? boundary.width : null,
      height: isHeightMaster ? boundary.height : null,
    };
    return (
      <div className="uploader__preview" style={{ height: boundary.height, width: boundary.width }} >
        <img // eslint-disable-line @next/next/no-img-element
          alt="preview"
          src={previewImageUrl}
          style={imageStyles}
          onLoad={onPreviewImageLoaded}
          onMouseDown={onImageMouseDown}
        />
      </div>
    );
  }

  const classes = classNames({
    [className]: !!className,
    'uploader': true,
    'uploader--grab': !!previewImageUrl,
    'uploader--grabbing': isGrabbing,
  });

  return (
    <div className={classes} style={{ backgroundImage: `url(${currentImageUrl})` }} >
      <Dropzone
        accept="image/*"
        disabled={!!previewImageUrl}
        multiple={false}
        noDrag={true}
        ref={dropzoneRef}
        onDrop={handleImageUpload}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps({ onClick: triggerPrivacyTipsModal })} // eslint-disable-line react/jsx-props-no-spreading
            style={{ border: 'none' }}
          >
            <input {...getInputProps()} /> {/* eslint-disable-line react/jsx-props-no-spreading */}
            {previewImageUrl && renderPreview()}
            {renderActionButtons()}
          </div>
        )}
      </Dropzone>
    </div>
  );
}
